import {
	Alert,
	Button,
	Grid,
	MenuItem,
	type TextFieldProps,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Field, Form, Formik, type FormikProps } from "formik";
import { TextField } from "formik-mui";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ButtonDocs } from "src/components/ButtonDocs";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { VARIATION_VARIABLE } from "src/config";
import { adornments } from "src/config/adornments";
import type { SegmentBulkMutationData } from "src/hooks/segments/useSegmentBulkUpdateMutation";
import { useSegmentBulkSchema } from "src/schemas/segment-bulk.schema";
import type { APIError, Segment, Timefactor, TimefactorType } from "src/types";
import {
	helperTextValue,
	helperTextValueWithOptions,
	helperTextVehicleCountsValue,
	propertyValue,
	propertyVehicleCountsValue,
} from "src/utils/segment-bulk-form.utils";
import {
	toInitialValues,
	unprepareVehicleCounts,
} from "src/utils/segments-bulk-expression.utils";

export type SegmentBulkFormData = {
	roadType: string | number;
	height_m: string | number;
	speed_km_h: string | number;
	vehicleCounts: Array<{
		vehicleType: string;
		count: string | number;
	}>;
};

interface Props {
	title?: string;
	readOnly?: boolean;
	segments: Segment[];
	initialValues: SegmentBulkMutationData | null;
	timefactorType: TimefactorType;
	vehicleTypes: string[];
	barrierHeights: number[];
	roadTypes: string[];
	timefactors: Timefactor[];
	onClose: () => void;
	onSubmit: (values: SegmentBulkFormData) => any;
	onDelete?: () => void;
	isLoading: boolean;
	errors?: APIError[] | null;
	formRef?: React.MutableRefObject<FormikProps<SegmentBulkFormData> | null>;
	isBaselineScenario?: boolean;
}

export function SegmentBulkForm(props: Props) {
	const { t } = useTranslation();
	const { isBaselineScenario = false } = props;
	const schema = useSegmentBulkSchema();

	const filteredTimefactors = props.timefactors.filter(
		(timefactor) => timefactor.type === "ROAD",
	);

	const initialValues: SegmentBulkFormData = useMemo(() => {
		if (props.initialValues) {
			return unprepareVehicleCounts(
				toInitialValues<
					SegmentBulkFormData & {
						vehicleCounts: { [key: string]: string | number };
					}
				>(props.initialValues.propertyExpressionList),
			);
		}
		return {
			roadType: propertyValue(props.segments, "roadType"),
			ifdmTimeFactor: propertyValue(props.segments, "ifdmTimeFactor"),
			barrierHeight_m: propertyValue(props.segments, "barrierHeight_m"),
			height_m: propertyValue(props.segments, "height_m"),
			speed_km_h: propertyValue(props.segments, "speed_km_h"),
			vehicleCounts: props.vehicleTypes.map((vehicleType) => {
				return {
					vehicleType,
					count: propertyVehicleCountsValue(props.segments, vehicleType),
				};
			}),
		};
	}, [props.initialValues, props.segments, props.vehicleTypes]);

	return render();

	function render() {
		return (
			<Formik
				innerRef={props.formRef}
				initialValues={initialValues}
				validationSchema={schema}
				enableReinitialize
				onSubmit={async (values: SegmentBulkFormData) => {
					return props.onSubmit(values);
				}}
			>
				{({ isSubmitting }) => (
					<Styles>
						<Grid container spacing={3}>
							{props.title && (
								<Grid item xs={12}>
									<Typography variant="h2">{props.title}</Typography>
								</Grid>
							)}

							<Grid item xs={12}>
								<ButtonDocs docsPath="/tutorial/scenario/scenario-emissions#bulk-edit-segments">
									{t("label.moreInfoAboutBulkEdit")}
								</ButtonDocs>
							</Grid>
							<Grid item xs={12}>
								<Typography variant="body1">
									{t("label.trafficCount")}
								</Typography>
								<Typography variant="body2" color="textSecondary">
									{t("label.timefactorType")}:{" "}
									{t(`label.timefactorTypes.${props.timefactorType}`)}
								</Typography>
							</Grid>

							{props.vehicleTypes.map((vehicleType, i) => {
								return (
									<Grid
										item
										xs={12}
										key={["vehicleCounts", vehicleType].join()}
									>
										<Field
											component={TextField}
											name={`vehicleCounts[${i}].count`}
											label={t(`label.vehicleType.${vehicleType}`)}
											variant="outlined"
											fullWidth
											disabled={props.readOnly}
											helperText={
												!isBaselineScenario
													? helperTextVehicleCountsValue(
															props.segments,
															vehicleType,
															t,
														)
													: ""
											}
										/>
									</Grid>
								);
							})}

							<Grid item xs={12}>
								<Typography>{t("label.segmentProperties")}</Typography>
							</Grid>

							<Grid item xs={12}>
								<Field
									component={TextField}
									name="speed_km_h"
									label={t("label.speedLimit")}
									variant="outlined"
									InputProps={adornments(t).kmPerHour}
									fullWidth
									disabled={props.readOnly}
									helperText={
										!isBaselineScenario
											? helperTextValue(props.segments, "roadTypeOld", t)
											: ""
									}
								/>
							</Grid>

							<Grid item xs={12}>
								<Field
									component={TextField}
									name="height_m"
									label={t("label.height")}
									variant="outlined"
									InputProps={adornments(t).meter}
									fullWidth
									disabled={props.readOnly}
									helperText={
										!isBaselineScenario
											? helperTextValue(props.segments, "heightOld_m", t)
											: ""
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<SelectField
									isBaselineScenario={isBaselineScenario}
									data={props.segments}
									options={props.roadTypes.map((roadType) => ({
										value: roadType,
										label: t(`label.roadTypeDict.${roadType}`),
									}))}
									name="roadType"
									label={t("label.roadType")}
									property="roadType"
									oldProperty="roadTypeOld"
									readOnly={props.readOnly}
								/>
							</Grid>
							<Grid item xs={12}>
								<SelectField
									isBaselineScenario={isBaselineScenario}
									name="ifdmTimeFactor"
									label={t("label.timefactor")}
									data={props.segments}
									options={filteredTimefactors.map((t) => ({
										value: t.nameKey,
										label: t.nameTranslation,
									}))}
									property="ifdmTimeFactor"
									oldProperty="ifdmTimeFactorOld"
									readOnly={props.readOnly}
								/>
							</Grid>
							<Grid item xs={12}>
								<SelectField
									isBaselineScenario={isBaselineScenario}
									name="barrierHeight_m"
									className="select-with-adornment"
									label={t("label.barrierHeight")}
									data={props.segments}
									options={props.barrierHeights.map((barrierHeight) => ({
										value: barrierHeight,
										label: barrierHeight.toString(),
									}))}
									property="barrierHeight_m"
									InputProps={adornments(t).meter}
									oldProperty="barrierHeightOld_m"
									readOnly={props.readOnly}
								/>
							</Grid>

							{props.errors && props.errors.length > 0 && (
								<Grid item xs={12}>
									<Alert severity="error" icon={false}>
										t('label.bulkEditValidationErrorsWithoutDefaultValues')
									</Alert>
									<ServerErrors data={props.errors} />
								</Grid>
							)}
							{!props.readOnly && (
								<>
									<ButtonGrid
										container
										item
										xs={12}
										sx={{ justifyContent: "flex-end" }}
									>
										<Button variant="text" onClick={props.onClose}>
											{t("label.cancel")}
										</Button>
										<ButtonLoading
											isLoading={isSubmitting || props.isLoading}
											type="submit"
											color="primary"
											variant="contained"
										>
											{t("label.save")}
										</ButtonLoading>
									</ButtonGrid>

									<ButtonGrid
										container
										item
										xs={12}
										sx={{ justifyContent: "flex-end" }}
									>
										{props.onDelete && (
											<DeleteButton variant="text" onClick={props.onDelete}>
												{t("label.deleteAll")}
											</DeleteButton>
										)}
									</ButtonGrid>
								</>
							)}

							{props.readOnly && (
								<ButtonGrid
									container
									item
									xs={12}
									sx={{ justifyContent: "flex-end" }}
								>
									<Button variant="text" onClick={props.onClose}>
										{t("label.close")}
									</Button>
								</ButtonGrid>
							)}
						</Grid>
					</Styles>
				)}
			</Formik>
		);
	}
}

function RoadTypeField(props: {
	data: Segment[];
	roadTypes: string[];
	readOnly?: boolean;
}) {
	const { t } = useTranslation();

	const options = useMemo(() => {
		const roadType = propertyValue(props.data, "roadType");
		if (roadType === VARIATION_VARIABLE)
			return [VARIATION_VARIABLE, ...props.roadTypes];
		else return props.roadTypes;
	}, [props.data, props.roadTypes]);

	function label(option: string | number) {
		if (option === VARIATION_VARIABLE) return option;
		else return t(`label.roadTypeDict.${option}`);
	}

	return render();

	function render() {
		return (
			<Field
				component={TextField}
				name="roadType"
				label={t("label.roadType")}
				type="text"
				select={true}
				variant="outlined"
				fullWidth
				disabled={props.readOnly}
				helperText={helperTextValue(props.data, "roadTypeOld", t)}
			>
				{options.map((option) => (
					<MenuItem key={option} value={option}>
						{label(option)}
					</MenuItem>
				))}
			</Field>
		);
	}
}

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
`;

function SelectField(
	props: {
		data: Segment[];
		options: { value: string | number; label: string }[];
		name: string;
		label: string;
		property: "ifdmTimeFactor" | "roadType" | "barrierHeight_m";
		oldProperty: "ifdmTimeFactorOld" | "roadTypeOld" | "barrierHeightOld_m";
		isBaselineScenario: boolean;
		readOnly?: boolean;
	} & TextFieldProps,
) {
	const { t } = useTranslation();
	const {
		data,
		options,
		name,
		label,
		property,
		oldProperty,
		isBaselineScenario,
		readOnly,
		...textFieldProps
	} = props;

	const mergedOptions = useMemo(() => {
		const value = propertyValue(data, property);
		if (value === VARIATION_VARIABLE)
			return [
				{ value: VARIATION_VARIABLE, label: VARIATION_VARIABLE },
				...options,
			];
		return options;
	}, [data, options, property]);

	function getLabel(option: (typeof options)[number]) {
		if (option.value === VARIATION_VARIABLE) return option.value;
		return option.label;
	}

	return render();

	function render() {
		return (
			<Field
				component={TextField}
				name={name}
				label={label}
				type="text"
				select={true}
				variant="outlined"
				fullWidth
				disabled={props.readOnly}
				helperText={
					!isBaselineScenario
						? helperTextValueWithOptions(data, oldProperty, mergedOptions, t)
						: ""
				}
				{...textFieldProps}
			>
				{mergedOptions.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{getLabel(option)}
					</MenuItem>
				))}
			</Field>
		);
	}
}

const Styles = styled(Form)`
  .select-with-adornment .MuiInputAdornment-root {
    margin-right: 16px;
  }
`;
